import { Component } from 'react'
import type { ReactNode, ErrorInfo } from 'react'
import { logError } from '@/lib/globalErrorHandlers'
import { AppError, type ErrorAction } from '@/lib/appError'

// ---------------------------------------------------------------------------
// RouteErrorBoundary
// ---------------------------------------------------------------------------

interface Props {
  children: ReactNode
}

interface State {
  hasError: boolean
  error?: Error
  hasUser: boolean
}

export class RouteErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props)
    this.state = { hasError: false, hasUser: false }
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error, hasUser: false }
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    // Log the error and its component stack, if present.
    logError(error, 'RouteErrorBoundary', info.componentStack ?? undefined)
    // Check for an active session to label the signout action correctly.
    import('@/lib/supabase/client').then(({ createClient }) => {
      createClient().auth.getUser().then(({ data }) => {
        this.setState({ hasUser: !!data.user })
      })
    })
  }

  render() {
    if (this.state.hasError) {
      const error = this.state.error
      const actions: ReadonlyArray<ErrorAction> =
        error instanceof AppError ? error.actions : ['retry', 'reload']

      return (
        <div className="min-h-screen bg-gray-950 flex items-center justify-center p-6">
          <div
            role="alert"
            aria-labelledby="route-error-heading"
            className="max-w-md w-full bg-gray-900 rounded-2xl p-6 border border-gray-800 text-center"
          >
            <p className="text-3xl mb-3" aria-hidden="true">⚠️</p>
            <h2 id="route-error-heading" className="text-white text-lg font-bold mb-2">Something went wrong</h2>
            <p className="text-gray-400 text-sm mb-4 whitespace-pre-line">
              {error?.message || 'An unexpected error occurred.'}
            </p>
            <div className="flex gap-3 justify-center">
              {actions.includes('retry') && (
                <button
                  onClick={() => this.setState({ hasError: false, error: undefined })}
                  className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg text-sm font-medium transition-colors"
                >
                  Try Again
                </button>
              )}
              {actions.includes('reload') && (
                <button
                  onClick={() => {
                    if ('serviceWorker' in navigator) {
                      navigator.serviceWorker.getRegistration().then(reg => {
                        if (reg) reg.update()
                      })
                    }
                    window.location.reload()
                  }}
                  className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg text-sm font-medium transition-colors"
                >
                  Reload App
                </button>
              )}
              {actions.includes('signout') && (
                <button
                  onClick={async () => {
                    const { signOut } = await import('@/contexts/UserContext')
                    await signOut()
                    window.location.replace('/login')
                  }}
                  className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg text-sm font-medium transition-colors"
                >
                  {this.state.hasUser ? 'Sign Out' : 'Back to Login'}
                </button>
              )}
              {actions.includes('clearcache') && (
                <button
                  onClick={async () => {
                    const { clearAllCaches } = await import('@/lib/offlineStore')
                    await clearAllCaches()
                    window.location.replace('/login')
                  }}
                  className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg text-sm font-medium transition-colors"
                >
                  Clear Cache
                </button>
              )}
            </div>
            <p className="text-gray-600 text-xs mt-4">
              If this keeps happening, try: Settings → Clear cache, then reopen the app.
            </p>
          </div>
        </div>
      )
    }
    return this.props.children
  }
}
